'use client'

import React, {type FC, useEffect, useRef, useState} from 'react'
import {Button} from './button'
import {Popover, PopoverContent, PopoverTrigger} from './popover'
import {Calendar} from './calendar'
import {DateInput} from './date-input'
import {Label} from './label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from './select'
import {Switch} from './switch'
import {CheckIcon, ChevronDownIcon, ChevronUpIcon} from '@radix-ui/react-icons'
import {cn} from '@/shared/lib/utils'
import {DateRange as DayPickerDateRange} from 'react-day-picker'

export interface DateRangePickerProps {
    onUpdate?: (values: { range: DateRange, rangeCompare?: DateRange }) => void
    initialDateFrom?: Date | string | null
    initialDateTo?: Date | string | null
    initialCompareFrom?: Date | string | null
    initialCompareTo?: Date | string | null
    align?: 'start' | 'center' | 'end'
    locale?: string
    showCompare?: boolean
}

interface CalendarInnerProps {
    mode: 'range' | 'single';
    selected: DayPickerDateRange | Date | undefined;
    onSelect: (value: DayPickerDateRange | Date | undefined) => void;
    isCalendarDisabled: boolean;
    isSmallScreen: boolean;
    defaultMonth: Date;
}


const CalendarInner: FC<CalendarInnerProps> = ({
                                                   mode,
                                                   selected,
                                                   onSelect,
                                                   isCalendarDisabled,
                                                   isSmallScreen,
                                                   defaultMonth
                                               }) => {
    // TypeScript musi być przekonany, że DayPickerProps są spójne.
    // Używamy "mode" jako literału, by DayPickerProps były poprawnie typowane.

    if (mode === 'range') {
        return (
            <Calendar
                mode="range"
                selected={selected as DayPickerDateRange | undefined}
                onSelect={onSelect as (value: DayPickerDateRange | undefined) => void}
                disabled={isCalendarDisabled}
                numberOfMonths={isSmallScreen ? 1 : 2}
                defaultMonth={defaultMonth}
            />
        );
    }

    // mode === 'single'
    return (
        <Calendar
            mode="single"
            selected={selected as Date | undefined}
            onSelect={onSelect as (value: Date | undefined) => void}
            disabled={isCalendarDisabled}
            numberOfMonths={isSmallScreen ? 1 : 2}
            defaultMonth={defaultMonth}
        />
    );
}

const formatDate = (date: Date, locale: string = 'en-us'): string => {
    return date.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    })
}

const getDateAdjustedForTimezone = (dateInput: Date | string | null | undefined): Date | null => {
    if (!dateInput) {
        return null;
    }
    if (typeof dateInput === 'string') {
        const parts = dateInput.split('-').map((part) => parseInt(part, 10))
        return new Date(parts[0], parts[1] - 1, parts[2])
    } else {
        return dateInput
    }
}

interface DateRange {
    from: Date | null
    to: Date | null
}

type SelectionMode = 'range' | 'open_ended' | 'no_date';
type OpenEndedPreset = 'to_end' | 'from_beginning';

interface Preset {
    name: string
    label: string
}

const RANGE_PRESETS: Preset[] = [
    {name: 'today', label: 'Dzisiaj'},
    {name: 'yesterday', label: 'Wczoraj'},
    {name: 'lastWeek', label: 'Ostatni tydzień'},
    {name: 'lastMonth', label: 'Ostatni miesiąc'},
]

const OPEN_ENDED_PRESETS: Preset[] = [
    {name: 'to_end', label: 'Od wybranego dnia do końca'},
    {name: 'from_beginning', label: 'Od początku do wybranego dnia'},
]

const getInitialMode = (
    from: Date | string | null,
    to: Date | string | null
): { mode: SelectionMode; openPreset: OpenEndedPreset } => {
    if (from && to) {
        return {mode: 'range', openPreset: 'to_end'};
    }
    if (from && !to) {
        return {mode: 'open_ended', openPreset: 'to_end'};
    }
    if (!from && to) {
        return {mode: 'open_ended', openPreset: 'from_beginning'};
    }
    // Zmieniono domyślny na 'range' jeśli oba są null, aby w pełni wykorzystać DayPicker'a
    return {mode: 'range', openPreset: 'to_end'};
}

/** The DateRangePicker component allows a user to select a range of dates */
export const DateRangePicker: FC<DateRangePickerProps> & {
    filePath: string
} = ({
         initialDateFrom = null,
         initialDateTo = null,
         initialCompareFrom = null,
         initialCompareTo = null,
         onUpdate,
         align = 'end',
         locale = 'en-US',
         showCompare = true
     }) => {
    const [isOpen, setIsOpen] = useState(false)

    const [range, setRange] = useState<DateRange>({
        from: getDateAdjustedForTimezone(initialDateFrom),
        to: getDateAdjustedForTimezone(initialDateTo)
    })
    const [rangeCompare, setRangeCompare] = useState<DateRange | undefined>(
        initialCompareFrom
            ? {
                from: getDateAdjustedForTimezone(initialCompareFrom),
                to: getDateAdjustedForTimezone(initialCompareTo)
            }
            : undefined
    )

    const {mode: initialMode, openPreset: initialOpenPreset} = getInitialMode(initialDateFrom, initialDateTo);
    const [selectionMode, setSelectionMode] = useState<SelectionMode>(initialMode);
    const [openEndedSubPreset, setOpenEndedSubPreset] = useState<OpenEndedPreset>(initialOpenPreset);
    const lastClickedOpenEndedDate = useRef<Date | null>(range.from || range.to || new Date());


    const openedRangeRef = useRef<DateRange | undefined>(undefined)
    const openedRangeCompareRef = useRef<DateRange | undefined>(undefined)

    const [selectedRangePreset, setSelectedRangePreset] = useState<string | undefined>(undefined)

    const [isSmallScreen, setIsSmallScreen] = useState(
        typeof window !== 'undefined' ? window.innerWidth < 960 : false
    )

    useEffect(() => {
        const handleResize = (): void => {
            setIsSmallScreen(window.innerWidth < 960)
        }
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const getStartOfDay = (date: Date) => {
        const newDate = new Date(date);
        newDate.setHours(0, 0, 0, 0);
        return newDate;
    }

    const getRangePresetRange = (presetName: string): DateRange => {
        const preset = RANGE_PRESETS.find(({name}) => name === presetName)
        if (!preset) throw new Error(`Unknown date range preset: ${presetName}`)

        const today = new Date();
        const startOfToday = getStartOfDay(today);
        let from: Date | null = startOfToday;
        let to: Date | null = startOfToday;

        switch (preset.name) {
            case 'today':
                from = startOfToday;
                to = new Date(today);
                to.setHours(23, 59, 59, 999);
                break
            case 'yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                from = getStartOfDay(yesterday);
                to = new Date(yesterday);
                to.setHours(23, 59, 59, 999);
                break
            case 'lastWeek':
                const lastWeekStart = new Date(today);
                lastWeekStart.setDate(today.getDate() - 7 - today.getDay());
                from = getStartOfDay(lastWeekStart);

                const lastWeekEnd = new Date(today);
                lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
                to = new Date(lastWeekEnd);
                to.setHours(23, 59, 59, 999);
                break
            case 'lastMonth':
                const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                from = getStartOfDay(lastMonthStart);

                const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                to = new Date(lastMonthEnd);
                to.setHours(23, 59, 59, 999);
                break
        }
        return {from, to}
    }

    const formatRangeLabel = (range: DateRange, locale: string): string => {
        const {from, to} = range;

        if (from && to) {
            return `${formatDate(from, locale)} - ${formatDate(to, locale)}`;
        } else if (from && !to) {
            return `od ${formatDate(from, locale)}`;
        } else if (!from && to) {
            return `do ${formatDate(to, locale)}`;
        } else {
            return "Brak daty";
        }
    };

    const setRangePreset = (preset: string): void => {
        const newRange = getRangePresetRange(preset)
        setRange(newRange)
        if (rangeCompare) {
            const newCompareFrom = newRange.from ? new Date(newRange.from.getFullYear() - 1, newRange.from.getMonth(), newRange.from.getDate()) : null;
            const newCompareTo = newRange.to ? new Date(newRange.to.getFullYear() - 1, newRange.to.getMonth(), newRange.to.getDate()) : null;
            setRangeCompare({from: newCompareFrom, to: newCompareTo});
        }
    }

    const checkRangePreset = (): void => {
        // Presety są sprawdzane tylko dla trybu 'range'
        if (selectionMode !== 'range') {
            setSelectedRangePreset(undefined);
            return;
        }

        for (const preset of RANGE_PRESETS) {
            const presetRange = getRangePresetRange(preset.name)

            const rangeFromTime = range.from ? getStartOfDay(range.from).getTime() : null;
            const rangeToTime = range.to ? getStartOfDay(range.to).getTime() : null;
            const presetFromTime = presetRange.from ? getStartOfDay(presetRange.from).getTime() : null;
            const presetToTime = presetRange.to ? getStartOfDay(presetRange.to).getTime() : null;

            if (
                rangeFromTime === presetFromTime &&
                rangeToTime === presetToTime
            ) {
                setSelectedRangePreset(preset.name)
                return
            }
        }
        setSelectedRangePreset(undefined)
    }

    const isRangeFromDisabled = selectionMode === 'no_date' || (selectionMode === 'open_ended' && openEndedSubPreset === 'from_beginning');
    const isRangeToDisabled = selectionMode === 'no_date' || (selectionMode === 'open_ended' && openEndedSubPreset === 'to_end');

    const resetValues = (): void => {
        const {mode: initialMode, openPreset: initialOpenPreset} = getInitialMode(initialDateFrom, initialDateTo);
        setSelectionMode(initialMode);
        setOpenEndedSubPreset(initialOpenPreset);

        setRange({
            from: getDateAdjustedForTimezone(initialDateFrom),
            to: getDateAdjustedForTimezone(initialDateTo)
        })
        setRangeCompare(
            initialCompareFrom
                ? {
                    from: getDateAdjustedForTimezone(initialCompareFrom),
                    to: getDateAdjustedForTimezone(initialCompareTo)
                }
                : undefined
        )
    }

    useEffect(() => {
        checkRangePreset()
    }, [range, selectionMode])

    const PresetButton = ({
                              label,
                              isSelected,
                              onClick
                          }: {
        label: string
        isSelected: boolean
        onClick: () => void;
    }) => (
        <Button
            className={cn(isSelected && 'pointer-events-none')}
            variant="ghost"
            onClick={onClick}
        >
            <>
                <span className={cn('pr-2 opacity-0', isSelected && 'opacity-70')}>
                  <CheckIcon width={18} height={18}/>
                </span>
                {label}
            </>
        </Button>
    )

    const areRangesEqual = (a?: DateRange, b?: DateRange): boolean => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        const aFrom = a.from?.getTime() ?? null;
        const aTo = a.to?.getTime() ?? null;
        const bFrom = b.from?.getTime() ?? null;
        const bTo = b.to?.getTime() ?? null;
        return aFrom === bFrom && aTo === bTo;
    }

    useEffect(() => {
        if (isOpen) {
            openedRangeRef.current = range
            openedRangeCompareRef.current = rangeCompare
        }
    }, [isOpen])

    const handleCompareCheckedChange = (checked: boolean) => {
        if (checked) {
            const baseFrom = range.from ?? new Date();
            const baseTo = range.to ?? baseFrom;
            setRangeCompare({
                from: new Date(baseFrom.getFullYear() - 1, baseFrom.getMonth(), baseFrom.getDate()),
                to: new Date(baseTo.getFullYear() - 1, baseTo.getMonth(), baseTo.getDate())
            });
        } else {
            setRangeCompare(undefined);
        }
    };

    const handleRangeFromChange = (date: Date | null) => {
        const currentTo = range.to ?? null;
        if (date && currentTo && date > currentTo) {
            setRange({from: date, to: date});
        } else {
            setRange(prev => ({...prev, from: date}));
        }
    };

    const handleRangeToChange = (date: Date | null) => {
        const currentFrom = range.from ?? null;
        if (date && currentFrom && date < currentFrom) {
            setRange({from: date, to: date});
        } else {
            setRange(prev => ({...prev, to: date}));
        }
    };

    // Logika zmiany głównego trybu
    const handleSelectionModeChange = (value: SelectionMode) => {
        setSelectionMode(value);

        switch (value) {
            case 'range':
                // Jeśli był tryb otwarty/brak daty, ustawmy sensowny zakres (np. dziś)
                if (range.from === null && range.to === null) {
                    setRange(getRangePresetRange('today'));
                }
                // Nie ruszamy 'rangeCompare', jeśli był aktywny
                break;
            case 'open_ended':
                // Domyślne ustawienie dla trybu otwartego
                setOpenEndedSubPreset('to_end');
                // Ustaw domyślnie 'od dziś do końca'
                const today = getStartOfDay(new Date());
                setRange({from: today, to: null});
                lastClickedOpenEndedDate.current = today; // ZAPAMIĘTAJ KLIKNIĘCIE
                break;
            case 'no_date':
                setRange({from: null, to: null});
                break;
        }
    }

    // ----------------------------------------------------
    // NOWA LOGIKA: Dynamiczny tryb kalendarza i obsługa selekcji
    // ----------------------------------------------------

    // 1. Dynamiczny tryb: range lub single
    const calendarMode = selectionMode === 'range' ? 'range' : 'single';

    // 2. Funkcja zwracająca odpowiednią wartość dla propa 'selected'
    const getCalendarSelected = (): DayPickerDateRange | Date | undefined => {
        if (calendarMode === 'range') {
            // Tryb normalny - DayPicker oczekuje { from, to }
            return {
                from: range.from ?? undefined,
                to: range.to ?? undefined
            } as DayPickerDateRange; // Wymagane rzutowanie
        }

        // Tryb 'single' (dla 'open_ended') - DayPicker oczekuje Date
        if (openEndedSubPreset === 'from_beginning') {
            return range.to ?? undefined; // Wybrana data jest datą 'do'
        }
        if (openEndedSubPreset === 'to_end') {
            return range.from ?? undefined; // Wybrana data jest datą 'od'
        }
        return undefined;
    };

    // 3. Zaktualizowana obsługa wyboru daty
    const handleCalendarSelect = (
        value: DayPickerDateRange | Date | undefined // Może być Date (czyli Date) lub DayPickerDateRange
    ) => {
        // Logika dla trybu 'range'
        if (calendarMode === 'range') {
            const rangeValue = value as DayPickerDateRange | undefined;
            setRange({
                from: rangeValue?.from ?? null,
                to: rangeValue?.to ?? null,
            });
            // Przy wyborze zakresu, aktualizujemy refa na wszelki wypadek
            lastClickedOpenEndedDate.current = rangeValue?.from ?? rangeValue?.to ?? null;

        } else {
            // Logika dla trybu 'single' (dla 'open_ended')
            const singleValue = value as Date | undefined;

            // ZAPISUJEMY KLIKNIĘTĄ DATĘ do refa i ustawiamy właściwy zakres
            lastClickedOpenEndedDate.current = singleValue ?? null;

            if (openEndedSubPreset === 'from_beginning') {
                // Od początku do (data)
                setRange({
                    from: null,
                    to: singleValue ?? null
                });
            } else if (openEndedSubPreset === 'to_end') {
                // Od (data) do końca
                setRange({
                    from: singleValue ?? null,
                    to: null
                });
            }
        }
    }

    const isCalendarDisabled = selectionMode === 'no_date'; // Zostało bez zmian

    // ----------------------------------------------------
    // Koniec nowej logiki
    // ----------------------------------------------------

    // Renderowanie dynamicznych list presetów
    const renderPresets = () => {
        if (isSmallScreen) return null;

        switch (selectionMode) {
            case 'range':
                return RANGE_PRESETS.map((preset) => (
                    <PresetButton
                        key={preset.name}
                        label={preset.label}
                        isSelected={selectedRangePreset === preset.name}
                        onClick={() => setRangePreset(preset.name)}
                    />
                ));
            case 'open_ended':
                return OPEN_ENDED_PRESETS.map((preset) => (
                    <PresetButton
                        key={preset.name}
                        label={preset.label}
                        isSelected={openEndedSubPreset === preset.name}
                        onClick={() => {
                            setOpenEndedSubPreset(preset.name as OpenEndedPreset)

                            // Używamy istniejącej daty, jeśli jest, lub ostatnio klikniętej, lub dziś
                            const existingDate = range.from ?? range.to;
                            const baseDate = existingDate ?? lastClickedOpenEndedDate.current ?? getStartOfDay(new Date());

                            if (preset.name === 'to_end') {
                                setRange({from: baseDate, to: null});
                            } else {
                                setRange({from: null, to: baseDate});
                            }
                        }}
                    />
                ));
            case 'no_date':
            default:
                return null;
        }
    }

    // Logika dla małego ekranu (Select)
    const renderMobilePresetSelect = () => {
        if (!isSmallScreen) return null;

        const baseOptions = [
            {name: 'range', label: 'Przedział dat', isPreset: false},
            {name: 'open_to_end', label: 'Od początku do wybranego dnia', isPreset: false},
            {name: 'open_from_beginning', label: 'Od wybranego dnia do końca', isPreset: false},
            {name: 'no_date', label: 'Brak ustawionej daty', isPreset: false},
        ];

        let mobileSelectedValue: string;
        if (selectionMode === 'range') mobileSelectedValue = 'range';
        else if (selectionMode === 'no_date') mobileSelectedValue = 'no_date';
        else mobileSelectedValue = `open_${openEndedSubPreset}`;

        const handleMobileSelectChange = (value: string) => {
            const today = getStartOfDay(new Date());
            const baseDate = lastClickedOpenEndedDate.current ?? today;

            if (value === 'range') {
                handleSelectionModeChange('range');
            } else if (value === 'no_date') {
                handleSelectionModeChange('no_date');
            } else if (value === 'open_to_end') {
                setSelectionMode('open_ended');
                setOpenEndedSubPreset('to_end');
                setRange({from: baseDate, to: null}); // Używamy zapamiętanej daty
            } else if (value === 'open_from_beginning') {
                setSelectionMode('open_ended');
                setOpenEndedSubPreset('from_beginning');
                setRange({from: null, to: baseDate}); // Używamy zapamiętanej daty
            } else {
                // Musi być preset z RANGE_PRESETS
                setSelectionMode('range');
                setRangePreset(value);
            }
        };


        return (
            <Select
                value={mobileSelectedValue}
                onValueChange={handleMobileSelectChange}
            >
                <SelectTrigger className="w-[180px] mx-auto mb-2">
                    <SelectValue placeholder="Wybierz tryb..."/>
                </SelectTrigger>
                <SelectContent>
                    {baseOptions.map((opt) => (
                        <SelectItem key={opt.name} value={opt.name}>
                            {opt.label}
                        </SelectItem>
                    ))}
                    {selectionMode === 'range' && RANGE_PRESETS.map((preset) => (
                        <SelectItem
                            key={preset.name}
                            value={preset.name}
                        >
                            ... {preset.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        )
    }

    return (
        <Popover
            modal={true}
            open={isOpen}
            onOpenChange={(open: boolean) => {
                if (!open) {
                    resetValues()
                }
                setIsOpen(open)
            }}
        >
            <PopoverTrigger asChild>
                <Button size="lg"
                        className="w-full max-w-full overflow-hidden text-left justify-between px-3 py-2"
                        variant="outline">
                    <div className="text-right">
                        <div className="py-1">
                            <div>{formatRangeLabel(range, locale)}</div>
                        </div>
                        {rangeCompare != null && (
                            <div className="opacity-60 text-xs -mt-1">
                                <>vs. {formatRangeLabel(rangeCompare, locale)}</>
                            </div>
                        )}
                    </div>
                    <div className="pl-1 opacity-60 -mr-2 scale-125">
                        {isOpen ? (<ChevronUpIcon width={24}/>) : (<ChevronDownIcon width={24}/>)}
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent align={align} className="w-auto">
                <div className="flex py-2">
                    <div className="flex">
                        <div className="flex flex-col">
                            {/* Główny przełącznik trybu (tylko desktop) */}
                            {!isSmallScreen && (
                                <div className="flex gap-4 px-3 pb-4 items-start">
                                    {/* Kolumna 1: Select (50% szerokości) */}
                                    <div className="w-1/2 flex-shrink-0">
                                        <Select value={selectionMode} onValueChange={handleSelectionModeChange}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue placeholder="Wybierz tryb..."/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="range">Przedział dat</SelectItem>
                                                <SelectItem value="open_ended">Od początku... / ...do końca</SelectItem>
                                                <SelectItem value="no_date">Brak ustawionej daty</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Kolumna 2: Compare Switch i DateInputs (50% szerokości) */}
                                    <div className="w-1/2 flex-shrink-0 flex flex-col gap-2">
                                        {showCompare && (
                                            <div className="flex items-center space-x-2 py-1 justify-end">
                                                <Switch
                                                    checked={Boolean(rangeCompare)}
                                                    onCheckedChange={handleCompareCheckedChange}
                                                    id="compare-mode"
                                                />
                                                <Label htmlFor="compare-mode">Compare</Label>
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-2">
                                            {/* DateInputs (pozostają w bloku flex, dla równego rozłożenia) */}
                                            <div className="flex gap-2">
                                                {/* Tutaj będą DateInputs - patrz Cel 2 */}
                                                <DateInput
                                                    value={range.from ?? null}
                                                    onChange={handleRangeFromChange}
                                                    disabled={isRangeFromDisabled} // NOWA ZMIENNA
                                                />
                                                <div className="py-1">-</div>
                                                <DateInput
                                                    value={range.to ?? null}
                                                    onChange={handleRangeToChange}
                                                    disabled={isRangeToDisabled} // NOWA ZMIENNA
                                                />
                                            </div>
                                            {/* Range Compare DateInputs (pozostaje bez zmian) */}
                                            {rangeCompare != null && (
                                                <div className="flex gap-2">
                                                    {/* ... logika DateInput dla porównania ... */}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Renderowanie selecta dla mobilki */}
                            {renderMobilePresetSelect()}

                            <div>
                                <CalendarInner
                                    mode={calendarMode}
                                    selected={getCalendarSelected()}
                                    onSelect={handleCalendarSelect}
                                    isCalendarDisabled={isCalendarDisabled}
                                    isSmallScreen={isSmallScreen}
                                    defaultMonth={
                                        range.from ??
                                        new Date(
                                            new Date().setMonth(
                                                new Date().getMonth() - (isSmallScreen ? 0 : 1)
                                            )
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {/* Dynamiczne presety dla desktopa */}
                    {!isSmallScreen && (
                        <div className="flex flex-col items-end gap-1 pr-2 pl-6 pb-6">
                            <div className="flex w-full flex-col items-end gap-1 pr-2 pl-6 pb-6">
                                {renderPresets()}
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 py-2 pr-4">
                    <Button
                        onClick={() => {
                            setIsOpen(false)
                            resetValues()
                        }}
                        variant="ghost"
                    >
                        Anuluj
                    </Button>
                    <Button
                        onClick={() => {
                            setIsOpen(false)
                            const isNoDateMode = selectionMode === 'no_date'

                            if (
                                isNoDateMode ||
                                !areRangesEqual(range, openedRangeRef.current) ||
                                !areRangesEqual(rangeCompare, openedRangeCompareRef.current)
                            ) {
                                onUpdate?.({
                                    range: range,
                                    rangeCompare: rangeCompare
                                });
                            }
                        }}
                    >
                        Zapisz zmiany
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    )
}

DateRangePicker.displayName = 'DateRangePicker'
DateRangePicker.filePath =
    'libs/shared/ui-kit/src/lib/date-range-picker/date-range-picker.tsx'